import Link from 'next/link'
import { isMobile } from 'react-device-detect'

import CancelModalFooter from '@/components/myTicket/ticketDetail/components/CancelModalFooter'
import CancelModalTitle from '@/components/myTicket/ticketDetail/components/CancelModalTitle'
import CancelFeeInfo from '@/components/purchase/components/airport/components/CancelFeeInfo'
import CarNumCheck from '@/components/purchase/components/airport/components/CarNumCheck'
import FadeInNotice from '@/components/purchase/components/airport/components/FadeInNotice'
import NoCompensation from '@/components/purchase/components/airport/components/NoCompensation'
import UserInfoCheck from '@/components/purchase/components/airport/components/UserInfoCheck'
import useAirportPurchaseViewModel from '@/components/purchase/components/airport/viewModel'
import PurchaseCompleteView from '@/components/purchase/components/shared/purchaseComplete/view'
import PurchaseIframe from '@/components/purchase/components/shared/purchaseIframe/view'
import PurchaseInComplete from '@/components/purchase/components/shared/purchaseInComplete/view'

import type { AirportTicketDetail } from '@/api/server/airport/dto/airportDto'
import Button from '@/shared/components/button/Button'
import Cover from '@/shared/components/Cover'
import FormInput from '@/shared/components/FormInput'
import Icons from '@/shared/components/icon/Icons'
import Input from '@/shared/components/input/Input'
import CheckBox from '@/shared/components/modal/CheckBox'
import Confirm from '@/shared/components/modal/Confirm'
import Modal from '@/shared/components/modal/Modal'
import ProcessContent from '@/shared/components/process/ProcessContent'
import ProcessFooter from '@/shared/components/process/ProcessFooter'
import ProcessHeader from '@/shared/components/process/ProcessHeader'
import SectionBar from '@/shared/components/SectionBar'
import Toast from '@/shared/components/Toast'
import { Caption, Text, Tint } from '@/shared/components/typography'
import { handleFormattingAddonLabel } from '@/shared/utils/generateEnumData'
import { phoneValidation, validateCarNum } from '@/shared/utils/regexPatterns'

const AirportPurchase = ({ data }: { data: AirportTicketDetail }) => {
  const { states, handlers, setters } = useAirportPurchaseViewModel({ data })

  return (
    <Cover
      id="layer3"
      isOpen={states.isPurchase}
      className="flex h-full w-full max-w-[768px] flex-col items-center"
      position="absolute"
    >
      {states.step === 0 && (
        <div className="flex h-full w-full flex-col">
          <ProcessHeader
            title="약관 동의"
            onBackClick={handlers.onClickTermsBackBtn}
            onCloseClick={handlers.handleCloseBtn}
          />
          <ProcessContent className="h-full w-full">
            <div className={`flex flex-1 justify-center`}>
              <div className="flex flex-1 flex-col items-center bg-010 pt-4">
                <Text level="text1" color="text090" weight="bold">
                  이용약관에 동의해주세요.
                </Text>
                <div className="mb-10 mt-6 flex w-full flex-1 flex-col gap-2 px-[16px]">
                  <div className="mx-[16px] my-4">
                    <CheckBox
                      onChange={handlers.handleAllCheckChange}
                      checked={states.allChecked}
                      text="전체 내용에 동의합니다."
                      color="text090"
                    />
                  </div>
                  <div className="flex flex-col rounded-lg bg-020 py-2">
                    {states.checkedItems.map((item, i) => (
                      <div key={i} className="mx-[16px] my-4 flex items-center justify-between gap-3">
                        <CheckBox
                          className="check"
                          onChange={(event) => handlers.handleCheckChange(event, i)}
                          checked={item.check}
                          text={item.text}
                          color="text090"
                        />
                        <Link className="min-w-[22px]" href={item.route} target="_blank">
                          <Tint level="tint1" style="underline" color="text070" className="whitespace-nowrap">
                            보기
                          </Tint>
                        </Link>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </ProcessContent>
          <ProcessFooter isDisabled={!states.allChecked} buttonText="다음" onClick={() => handlers.handleStep(1)} />
        </div>
      )}

      {states.step === 1 && (
        <div className="flex h-full w-full flex-col">
          <ProcessHeader
            title="차량번호 입력"
            onBackClick={() => handlers.handleStep(0)}
            onCloseClick={handlers.handleCloseBtn}
          />
          <ProcessContent className="h-full w-full">
            <div className={`flex flex-1 justify-center`}>
              <div className="flex w-full flex-col items-center bg-010 pt-4">
                <Text level="text1" color="text090" weight="bold">
                  차량번호를 입력해 주세요.
                </Text>
                <div className="mb-[9px] mt-10 flex w-full flex-col gap-2 px-4">
                  <Text level="text3" color="text090">
                    차량번호
                  </Text>
                  <Input
                    className={`border-b-[1px] border-060 pb-2 ${
                      states.carNum.length ? 'font-medium' : 'font-regular'
                    } leading-26 focus:border-main-050`}
                    value={states.carNum}
                    placeholder={'서울12가3456, 12가4567'}
                    onChange={handlers.handleCarNumChange}
                    length={9}
                    autoFocus={true}
                  />
                  {states.carNum.length >= 7 && !validateCarNum(states.carNum) && (
                    <Caption level="caption2" color="caution070">
                      올바른 차량번호 형식으로 입력해 주세요.
                    </Caption>
                  )}
                  <div className="mt-6 rounded-md bg-020 px-3 py-3">
                    <ol className="list-disc">
                      <li className="list-style-desc-car">
                        <Caption level="caption2" color="text090">
                          차량번호를 정확히 입력해 주세요.
                        </Caption>
                      </li>
                      <li className="list-style-desc-car">
                        <Caption level="caption2" color="text090">
                          임시번호판 차량은 이용이 불가합니다.
                        </Caption>
                      </li>
                    </ol>
                  </div>
                </div>
              </div>
            </div>
          </ProcessContent>
          <ProcessFooter
            buttonText="다음"
            isDisabled={!validateCarNum(states.carNum)}
            onClick={handlers.handleCarNumCheck}
          />
        </div>
      )}
      {states.step === 2 && (
        <div className="flex h-full w-full flex-col">
          <ProcessHeader
            title="휴대폰 번호 인증"
            onBackClick={() => handlers.handleStep(1)}
            onCloseClick={handlers.handleCloseBtn}
          />
          <ProcessContent className="h-full w-full">
            <div className={`flex flex-1 justify-center`}>
              <div className="flex w-full flex-col items-center bg-010 px-4 pt-4">
                <Text level="text1" color="text090" weight="bold">
                  휴대폰 번호를 입력해 주세요.
                </Text>
                <div className="mb-[9px] mt-10 flex w-full flex-col gap-2">
                  <Text level="text3" color="text090">
                    휴대폰 번호
                  </Text>
                  <Input
                    className={`border-b-[1px] border-060 pb-2 ${
                      states.phoneNum.length ? 'font-medium' : 'font-regular'
                    } leading-26 focus:border-main-050`}
                    value={states.phoneNum}
                    placeholder={'숫자만 입력해 주세요.'}
                    onChange={handlers.handlePhoneChange}
                    autoFocus={true}
                    length={11}
                    mode="numeric"
                  />
                  {!phoneValidation(states.phoneNum) && states.phoneNum.length === 11 && (
                    <Caption level="caption2" color="caution070">
                      010으로 시작하는 휴대폰 번호를 입력해 주세요.
                    </Caption>
                  )}
                </div>
                {states.isSend && (
                  <div className="mt-6 flex w-full flex-col gap-2">
                    <Text level="text3" color="text090">
                      인증번호
                    </Text>
                    <div className="relative">
                      <Input
                        className={`border-b-[1px] border-060 pb-2 ${
                          states.authNum.length ? 'font-medium' : 'font-regular'
                        } leading-26 focus:border-main-050`}
                        value={states.authNum}
                        placeholder="0000"
                        onChange={handlers.handleAuthNumChange}
                        length={4}
                        autoFocus={false}
                        mode="numeric"
                      />
                      {states.isSend && (
                        <Caption
                          level="caption1"
                          className={`absolute right-1 top-2/4 -translate-y-2/4 ${
                            states.seconds === 0 ? 'text-caution-070' : 'text-main-050'
                          }`}
                        >
                          {handlers.expirationTime()}
                        </Caption>
                      )}
                    </div>
                    {states.isAuth === false && (
                      <Caption level="caption2" color="caution070">
                        {states.errorText}
                      </Caption>
                    )}
                  </div>
                )}
              </div>
            </div>
          </ProcessContent>
          <div className="flex w-full flex-col items-center gap-3 bg-010">
            {states.isSend && (
              <div onClick={handlers.requestAuthCodeAgain} className="cursor-pointer">
                <Caption level="caption1" color="text090" style="underline">
                  인증 번호 다시 받기
                </Caption>
              </div>
            )}
            <ProcessFooter
              buttonText={states.isSend ? '다음' : '인증번호 받기'}
              isDisabled={!phoneValidation(states.phoneNum) || (states.isSend && states.authNum.length !== 4)}
              onClick={states.isSend ? handlers.onClickPhoneNextBtn : handlers.handleSendAuthCode}
            />
          </div>
        </div>
      )}
      {states.step === 3 && (
        <div className="flex h-full w-full flex-col">
          <ProcessHeader
            title="결제하기"
            onBackClick={() => handlers.handleStep(2)}
            onCloseClick={handlers.handleCloseBtn}
          />
          <ProcessContent className="flex flex-col last:gap-0">
            <div className={`px-4 py-6`}>
              <Text level="text1" color="text090" weight="bold">
                {states.ticketData.couponName}
              </Text>
              <div className={`mt-3 flex flex-col gap-[10px]`}>
                <div className="flex justify-between">
                  <Caption level="caption1" color="text070">
                    주차장명
                  </Caption>
                  <Caption
                    level="caption1"
                    color="text090"
                    className={'overflow-hidden text-ellipsis whitespace-nowrap'}
                  >
                    {states.ticketData.parkinglotName}
                  </Caption>
                </div>
                <div className="flex justify-between">
                  <Caption level="caption1" color="text070">
                    차량번호
                  </Caption>
                  <Caption level="caption1" color="text090">
                    {states.carNum}
                  </Caption>
                </div>
                <div className="flex justify-between">
                  <Caption level="caption1" color="text070">
                    입차시간
                  </Caption>
                  <Caption level="caption1" color="text090">
                    {states.startDate}
                  </Caption>
                </div>
                <div className="flex justify-between">
                  <Caption level="caption1" color="text070">
                    출차시간
                  </Caption>
                  <Caption level="caption1" color="text090">
                    {states.endDate}
                  </Caption>
                </div>
              </div>
            </div>
            <SectionBar className="h-[8px]" />
            <div className={`flex w-full flex-col items-center py-6 ${states.isToggleOpen && 'pb-0'}`}>
              <div
                className={`flex w-full cursor-pointer items-center justify-between px-4 ${states.isToggleOpen && 'border-b border-b-030 pb-6'}`}
                onClick={handlers.handleToggle}
              >
                <Text level="text3" color="text090" weight="bold">
                  결제 금액
                </Text>
                <div className="flex items-center gap-2">
                  <Text level="text3" color="text090" weight="bold">
                    {states.totalPrice.toLocaleString()}원
                  </Text>
                  {states.isToggleOpen ? (
                    <Icons.ArrowUpIcon stroke="#000000" />
                  ) : (
                    <Icons.ArrowUpIcon stroke="#000000" className="rotate-180" />
                  )}
                </div>
              </div>
              <div
                className={`w-full px-4 py-6 transition-all duration-300 ease-in-out ${states.isToggleOpen ? 'block' : 'hidden'}`}
              >
                <div className="flex w-full flex-col gap-2">
                  <div className="flex w-full items-center justify-between">
                    <Text level="text3" color="text070">
                      상품금액
                    </Text>
                    <Text level="text3" color="text070">
                      {states.ticketData.basePrice.toLocaleString()}원
                    </Text>
                  </div>
                  {states.ticketData.addonPrice.map((addon, i) => (
                    <div key={i} className="flex w-full items-center justify-between">
                      <Text level="text3" color="text070">
                        {handleFormattingAddonLabel(addon.addonPolicyType)}
                      </Text>
                      <Text level="text3" color="text070">
                        {addon.price.toLocaleString()}원
                      </Text>
                    </div>
                  ))}
                </div>
              </div>
            </div>
            <SectionBar />
            <div className="flex flex-col gap-6 px-4 py-6">
              <Text level="text3" color="text090" weight="bold">
                결제 수단 선택
              </Text>
              <div className="flex w-full flex-col gap-4">
                {states.paymentList.map((payment, i) => (
                  <label key={i} className="flex cursor-pointer items-center text-17 leading-26 text-text-090">
                    <input
                      className={`radio mr-1 h-6 w-6 cursor-pointer ${
                        payment.checked ? 'bg-checkedRoundIcon' : 'bg-checkRoundIcon'
                      }`}
                      type="radio"
                      id={String(i)}
                      checked={payment.checked}
                      value={payment.value}
                      onChange={handlers.radioHandler}
                    />
                    {payment.value}
                  </label>
                ))}
              </div>
            </div>
            <SectionBar />
            <div className="flex w-full flex-col gap-2 py-6">
              <div className="px-4 pb-4 text-[17px] font-bold">
                <CheckBox
                  onChange={handlers.handlePolicyCheckChange}
                  checked={states.isChecked}
                  text="보상 규정 및 취소 수수료를 확인했습니다."
                  color="text090"
                />
              </div>
              <div className="flex flex-col rounded-lg">
                {states.policyItems.map((item, i) => (
                  <div key={i} className="flex items-center justify-between px-4 py-1 last:pb-4">
                    <Caption level="caption1" color="text070">
                      {item.text}
                    </Caption>
                    <div onClick={item.onClick} className="cursor-pointer">
                      <Tint level="tint1" style="underline" color="text070">
                        보기
                      </Tint>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </ProcessContent>
          <div className="relative">
            <ProcessFooter
              onClick={handlers.handlePaymentBtn}
              isDisabled={!states.isDisabled}
              buttonText={`${states.totalPrice.toLocaleString()}원 결제하기`}
            />
            <FadeInNotice />
          </div>
        </div>
      )}
      {states.isToastOpen && (
        <Toast
          text="인증 번호를 다시 전송했습니다."
          onClose={() => {
            setters.setIsToastOpen(false)
          }}
        />
      )}
      {states.isErrorToast && (
        <Toast
          text={states.toastText}
          onClose={() => {
            setters.setIsErrorToast(false)
          }}
          position="top"
        />
      )}
      <CarNumCheck
        isOpen={states.isCarNumChecked}
        onClose={handlers.handleCarNumCheck}
        onClick={handlers.confirmCarNumCheck}
        carNum={states.carNum}
      />
      <NoCompensation isOpen={states.isNoCompensationOpen} onClick={handlers.handleNoCompensationClick} />
      <CancelFeeInfo isOpen={states.isCancelFeeOpen} onClick={handlers.handleCancelFeeInfoClick} />
      <Modal targetId="#layer3" isOpen={states.isCancelModalOpen} onClose={handlers.onClickCancelModalConfirmBtn}>
        <CancelModalTitle title="주차권 구매를 그만하시겠어요?" />
        <Confirm
          bodyText="화면을 나갈 경우 작성한 정보들이 모두 사라집니다."
          className="my-5 break-keep px-2 text-center"
        />
        <CancelModalFooter
          onCloseCancelModal={handlers.onClickCancelModalCloseBtn}
          onClickCancelConfirmBtn={handlers.onClickCancelModalConfirmBtn}
          cancelTitle="나가기"
          confirmTitle="계속하기"
        />
      </Modal>
      {states.isNeedUserInfo && (
        <div className="absolute left-0 top-0 z-100 flex h-full w-full flex-1 flex-col bg-010">
          <ProcessHeader
            title="공항 주차 정보 입력"
            onBackClick={handlers.handleNeedUserInfoBtn}
            onCloseClick={handlers.handleUserInfoCloseBtn}
          />
          <ProcessContent className="px-4 pb-[200px] pt-4">
            <form className="flex w-full flex-col gap-5">
              {states.userInput.map((field, index) => (
                <FormInput
                  key={index}
                  title={field.title}
                  required={field.required}
                  register={states.register(field.title, {
                    required: field.required,
                    onChange: handlers.handleGetUserInputValue,
                    value: field.value
                  })}
                  inputProps={{
                    type: field.type,
                    placeholder: field.placeholder
                  }}
                />
              ))}
            </form>
          </ProcessContent>
          <ProcessFooter
            buttonText={'저장하기'}
            isDisabled={!states.isFormValid}
            onClick={handlers.handleFormNoticeOpen}
          />
        </div>
      )}
      <Modal targetId="body" isOpen={states.isNeedConfirm} onClose={handlers.handleUserInfoCloseBtn}>
        <div className="mt-6 px-2">
          <Text level="text1" color="text090" weight="medium">
            필수 정보 입력을 그만하시겠어요?
          </Text>
        </div>
        <Confirm
          bodyText={`주차권 이용일 3시간 전까지 입력하지 않을 경우 주차권 이용이 어려울 수 있습니다.`}
          className="pb-6 pt-5 text-center"
        />
        <div className="flex w-full items-center gap-2">
          <Button
            color="text090"
            background="bg020"
            text="입력 종료"
            pyValue="py-[13px]"
            onClick={handlers.handleNeedConfirmCloseBtn}
          />
          <Button
            color="text010"
            background="bg030"
            text="계속하기"
            pyValue="py-[13px]"
            onClick={handlers.handleUserInfoCloseBtn}
          />
        </div>
      </Modal>
      {states.isComplete && (
        <PurchaseCompleteView
          isComplete={states.isComplete}
          data={data}
          carNum={states.carNum}
          time={states.useTime}
          ticketSeq={states.ticketSeq}
          onClick={handlers.handleNeedUserInfoBtn}
        />
      )}
      {states.isInComplete && (
        <PurchaseInComplete isInComplete={states.isInComplete} onClick={handlers.handleRePayment} />
      )}
      {states.isFormNoticeOpen && (
        <UserInfoCheck
          isOpen={states.isFormNoticeOpen}
          onClick={handlers.handleFormNoticeConfirmBtn}
          onSecondClick={handlers.handleFormNoticeOpen}
          userInputs={states.userInputs}
        />
      )}
      {states.frameUrl && isMobile && <PurchaseIframe url={states.frameUrl} />}
      {states.frameUrl && states.isOpen && (
        <div className="absolute left-0 top-0 flex h-full w-full items-center justify-center">
          <iframe src={states.frameUrl} className="z-100 h-full w-[768px]" scrolling="no" />
        </div>
      )}
    </Cover>
  )
}

export default AirportPurchase
